import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { Damage } from '../../../../interfaces/Damages.interfaces';
import { DamageManagementService } from '../../../../services/damage-management.service';
import { DamageNavBoxComponent } from '../damage-nav-box/damage-nav-box.component';

/** Colonna della tabella delle pratiche assegnate */
interface AssignedColumn {
  key: keyof Damage | 'clientName';
  label: string;
  sortable: boolean;
  type: 'text' | 'date' | 'status' | 'severity' | 'number' | 'client';
}

/**
 * /damage-overview/assigned: le pratiche danno assegnate all'utente collegato
 * (damage_assigned_to = utente del token, risolto dal backend). Di base
 * mostra solo le pratiche aperte; il toggle "Includi chiuse" aggiunge
 * CLOSED e REJECTED. Stesso design di /damage-overview (header, strumentario,
 * resoconto a metric card, dmg-table, barra di paginazione): l'endpoint
 * restituisce tutte le pratiche in una volta, quindi ordinamento e paginazione
 * sono lato client. Dalla riga si apre il dettaglio.
 */
@Component({
  selector: 'app-assigned-damages',
  standalone: true,
  imports: [CommonModule, TranslateModule, DamageNavBoxComponent],
  templateUrl: './assigned-damages.component.html',
  styleUrls: ['./assigned-damages.component.css']
})
export class AssignedDamagesComponent implements OnInit {
  damages: Damage[] = [];
  /** Codice dell'utente di cui sono mostrate le pratiche (dal backend) */
  userCode = '';
  includeClosed = false;
  isLoading = false;
  errorMessage = '';
  hasLoaded = false;

  sortField: AssignedColumn['key'] = 'damageOpenedAt';
  sortAsc = false;

  private readonly STORAGE_KEY = 'assigned-damages-settings';
  isCompactTable = false;

  // ─── PAGINAZIONE (lato client, stessa barra di /damage-overview) ───
  currentPage  = 1;
  itemsPerPage = 25;

  readonly columns: AssignedColumn[] = [
    { key: 'damageCode',       label: 'Pratica',    sortable: true, type: 'text' },
    { key: 'damageParcelCode', label: 'Collo',      sortable: true, type: 'text' },
    { key: 'damageWaybillCode', label: 'Bolla',     sortable: true, type: 'text' },
    { key: 'clientName',       label: 'Cliente',    sortable: true, type: 'client' },
    { key: 'damageStatus',     label: 'Stato',      sortable: true, type: 'status' },
    { key: 'damageSeverity',   label: 'Gravità',    sortable: true, type: 'severity' },
    { key: 'damagePriority',   label: 'Priorità',   sortable: true, type: 'number' },
    { key: 'damageOpenedAt',   label: 'Aperta il',  sortable: true, type: 'date' },
    { key: 'damageDueDate',    label: 'Scadenza',   sortable: true, type: 'date' }
  ];

  /** Nome cliente per codice, dalla stessa sorgente di /damaged-parcels (userFunctions in localStorage) */
  private clientNameByCode = new Map<string, string>();

  constructor(
    private router: Router,
    private damageService: DamageManagementService
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'auto' });
    this.loadUserPreferences();
    this.loadClientNames();
    this.load();
  }

  private loadClientNames(): void {
    const stored = localStorage.getItem('userFunctions');
    if (!stored) return;
    try {
      const clients: { clientCode?: string; clientName?: string }[] = JSON.parse(stored)?.clients ?? [];
      this.clientNameByCode = new Map(
        clients
          .filter(c => c.clientCode && c.clientName)
          .map(c => [c.clientCode!, c.clientName!] as [string, string])
      );
    } catch {
      this.clientNameByCode = new Map();
    }
  }

  /** Carica (o ricarica) le pratiche assegnate all'utente collegato */
  load(): void {
    if (this.isLoading) return;
    this.isLoading    = true;
    this.errorMessage = '';
    this.damageService.getAssignedDamages(this.includeClosed).pipe(
      finalize(() => { this.isLoading = false; this.hasLoaded = true; })
    ).subscribe({
      next: res => {
        this.userCode    = res.userCode;
        this.damages     = res.damages;
        this.currentPage = 1;
      },
      error: (err: Error) => {
        this.damages      = [];
        this.errorMessage = err?.message || 'Errore nel caricamento delle pratiche assegnate. Riprova più tardi.';
      }
    });
  }

  setIncludeClosed(value: boolean): void {
    if (this.includeClosed === value) return;
    this.includeClosed = value;
    this.load();
  }

  openDetail(damage: Damage): void {
    this.router.navigate(['/damage-overview/detail', Number(damage.damageId)]);
  }

  // ─── RESOCONTO ───

  isClosed(damage: Damage): boolean {
    return damage.damageStatus === 'CLOSED' || damage.damageStatus === 'REJECTED';
  }

  isOverdue(damage: Damage): boolean {
    if (this.isClosed(damage) || !damage.damageDueDate) return false;
    const due = new Date(damage.damageDueDate);
    return !isNaN(due.getTime()) && due.getTime() < Date.now();
  }

  get openCount(): number        { return this.damages.filter(d => !this.isClosed(d)).length; }
  get inProgressCount(): number  { return this.damages.filter(d => d.damageStatus === 'IN_PROGRESS').length; }
  get waitingCount(): number     { return this.damages.filter(d => d.damageStatus === 'WAITING').length; }
  get overdueCount(): number     { return this.damages.filter(d => this.isOverdue(d)).length; }
  get closedCount(): number      { return this.damages.filter(d => this.isClosed(d)).length; }

  // ─── TABELLA ───

  /** Valore di una colonna data (stringa ISO dal backend), null se vuoto */
  dateValue(damage: Damage, col: AssignedColumn): string | null {
    const v = this.cellValue(damage, col);
    return typeof v === 'string' && v ? v : null;
  }

  /** Valore grezzo della cella (il nome cliente è risolto a parte) */
  cellValue(damage: Damage, col: AssignedColumn): unknown {
    return col.key === 'clientName' ? this.clientName(damage) : damage[col.key];
  }

  clientName(damage: Damage): string {
    const code = damage.damageClientCode ?? '';
    return this.clientNameByCode.get(code) ?? code;
  }

  /** Righe ordinate lato client per la colonna selezionata */
  get sortedDamages(): Damage[] {
    const field = this.sortField;
    const dir   = this.sortAsc ? 1 : -1;
    const value = (d: Damage): string | number => {
      if (field === 'clientName') return this.clientName(d).toLowerCase();
      const v = d[field];
      if (v == null) return this.sortAsc ? '￿' : '';
      if (typeof v === 'number') return v;
      return String(v).toLowerCase();
    };
    return [...this.damages].sort((a, b) => {
      const va = value(a), vb = value(b);
      if (va === vb) return 0;
      return va > vb ? dir : -dir;
    });
  }

  sortBy(field: AssignedColumn['key']): void {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc   = true;
    }
    this.currentPage = 1;
  }

  getSortIcon(field: AssignedColumn['key']): string {
    if (this.sortField !== field) return 'fas fa-sort text-muted';
    return this.sortAsc ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
  }


  // ─── PREFERENZE (densità tabella ed elementi per pagina) ───
  toggleCompactTable(): void {
    this.isCompactTable = !this.isCompactTable;
    this.saveUserPreferences();
  }

  private loadUserPreferences(): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const settings = JSON.parse(raw);
      if (typeof settings.isCompactTable === 'boolean') {
        this.isCompactTable = settings.isCompactTable;
      }
      if (typeof settings.itemsPerPage === 'number' && settings.itemsPerPage > 0) {
        this.itemsPerPage = settings.itemsPerPage;
      }
    } catch (error) {
      console.warn('Error loading assigned damages preferences:', error);
    }
  }

  private saveUserPreferences(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        isCompactTable: this.isCompactTable,
        itemsPerPage:   this.itemsPerPage
      }));
    } catch (error) {
      console.warn('Error saving assigned damages preferences:', error);
    }
  }

  // ─── PAGINAZIONE (lato client: le righe sono già tutte in pagina) ───
  get totalRecords(): number {
    return this.damages.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  /** Righe della pagina corrente, nell'ordine scelto */
  get pagedDamages(): Damage[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.sortedDamages.slice(start, start + this.itemsPerPage);
  }

  /** Numero progressivo della riga nell'intero elenco (non solo nella pagina) */
  rowNumber(index: number): number {
    return (this.currentPage - 1) * this.itemsPerPage + index + 1;
  }

  /** Numeri di pagina mostrati nella barra (finestra di 5 attorno alla corrente) */
  private getPageNumbers(): number[] {
    const totalPages = this.totalPages;
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  /** Numeri di pagina con le pagine estreme e i puntini (null) quando ce ne sono altre oltre la finestra */
  getPageItems(): (number | null)[] {
    const window = this.getPageNumbers();
    const total = this.totalPages;
    if (window.length === 0) return [];
    const items: (number | null)[] = [];
    const first = window[0];
    const last = window[window.length - 1];
    if (first > 1) {
      items.push(1);
      if (first > 2) items.push(null);
    }
    items.push(...window);
    if (last < total) {
      if (last < total - 1) items.push(null);
      items.push(total);
    }
    return items;
  }

  trackByPageItem(index: number, item: number | null): number {
    return item ?? -index - 1;
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
  }

  // Vai a pagina
  isPageJumpOpen = false;
  pageJumpValue = '';
  @ViewChild('pageJumpInput') pageJumpInput?: ElementRef<HTMLInputElement>;
  // Vai all'elemento
  isItemJumpOpen = false;
  itemJumpValue = '';
  @ViewChild('itemJumpInput') itemJumpInput?: ElementRef<HTMLInputElement>;

  openPageJump(): void {
    this.pageJumpValue = '';
    this.isPageJumpOpen = true;
    setTimeout(() => this.pageJumpInput?.nativeElement.focus());
  }

  closePageJump(): void {
    this.isPageJumpOpen = false;
  }

  applyPageJump(): void {
    const n = parseInt(this.pageJumpValue, 10);
    this.closePageJump();
    if (!isNaN(n)) {
      this.onPageChange(Math.min(Math.max(n, 1), this.totalPages));
    }
  }

  openItemJump(): void {
    this.itemJumpValue = '';
    this.isItemJumpOpen = true;
    setTimeout(() => this.itemJumpInput?.nativeElement.focus());
  }

  closeItemJump(): void {
    this.isItemJumpOpen = false;
  }

  applyItemJump(): void {
    const n = parseInt(this.itemJumpValue, 10);
    this.closeItemJump();
    if (!isNaN(n)) {
      const item = Math.min(Math.max(n, 1), Math.max(1, this.totalRecords));
      this.onPageChange(Math.ceil(item / this.itemsPerPage));
    }
  }

  setItemsPerPage(n: number): void {
    if (this.itemsPerPage === n) return;
    this.itemsPerPage = n;
    this.currentPage  = 1;
    this.saveUserPreferences();
  }


  statusBadgeClass(status?: string): string {
    switch (status) {
      case 'OPEN':        return 'dmg-badge--info';
      case 'IN_PROGRESS': return 'dmg-badge--pending';
      case 'WAITING':     return 'dmg-badge--medium';
      case 'CLOSED':      return 'dmg-badge--resolved';
      case 'REJECTED':    return 'dmg-badge--critical';
      default:            return 'dmg-badge--info';
    }
  }

  severityBadgeClass(severity?: string): string {
    switch (severity) {
      case 'CRITICAL': return 'dmg-badge--critical';
      case 'HIGH':     return 'dmg-badge--high';
      case 'MEDIUM':   return 'dmg-badge--medium';
      case 'LOW':      return 'dmg-badge--low';
      default:         return 'dmg-badge--info';
    }
  }

  trackByDamage(index: number, damage: Damage): number {
    return damage.damageId ?? index;
  }
}
